import { useCallback, useMemo } from 'react';
import { Button, Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import { IoAddCircle, IoEllipsisHorizontalCircleOutline, IoHammerOutline, IoRemoveCircle, IoWalletOutline } from 'react-icons/io5';
import base from '../../Services/BaseService';
import { FormatCurrencyCell } from '../Functions/NumberFormatter';
import LoadSpinner from '../Shared/ComponentLoading';
import { defaultGridColumnDef } from '../Shared/Grid';
import { AgGridReact } from 'ag-grid-react';

const currencyOptions = { style: 'currency', currency: 'GBP' };

const DashboardBudgets = ({ fieldData, budgetData, summaryLoading, budgetLoading, onRefresh, onManageBalance }) => {
    const ButtonRenderer = useCallback(({ data }) => {
        const handleTopUp = async (direction) => {
            await base.update(`Dashboard/topupbudget/${data.categoryId}/${direction}`);
            onRefresh();
        };

        return (
            <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
                <div className="p-0" title="Add £50" style={{ cursor: 'pointer' }} onClick={() => handleTopUp('up')}>
                    <IoAddCircle size={22} className="transaction-in-icon" />
                </div>
                <div className="p-0" title="Subtract £50" style={{ cursor: 'pointer' }} onClick={() => handleTopUp('down')}>
                    <IoRemoveCircle size={22} className="transaction-out-icon" />
                </div>
                <Button color="link" className="p-0" title="Manage balance" aria-label={`Manage balance for ${data.categoryId}`} onClick={() => onManageBalance(data)}>
                    <IoEllipsisHorizontalCircleOutline size={22} style={{ color: '#111' }} />
                </Button>
            </div>
        );
    }, [onManageBalance, onRefresh]);

    const columnDefs = useMemo(() => [
        { headerName: 'Category', field: 'categoryId', suppressMovable: true, maxWidth: 95 },
        { headerName: 'Description', field: 'categoryDescription', suppressMovable: true, maxWidth: 500 },
        {
            headerName: 'Amount', field: 'totalRemaining', suppressMovable: true,
            filter: 'agNumberColumnFilter', type: 'numericColumn', maxWidth: 125,
            valueFormatter: FormatCurrencyCell,
        },
        {
            headerName: 'Top-up', filter: false, maxWidth: 115, cellRenderer: ButtonRenderer,
            cellStyle: { display: 'flex', justifyContent: 'center' },
        },
    ], [ButtonRenderer]);

    const quickFixExceeded = async () => {
        await base.update('Dashboard/quickfixbudget');
        onRefresh();
    };

    return (
        <Card className="mt-2">
            <CardHeader className="cd-header">
                <Row>
                    <Col>
                        <div className="d-flex">
                            <IoWalletOutline size={24} style={{ color: '#111' }} />&nbsp;&nbsp;<div>Budgets</div>
                        </div>
                    </Col>
                    <Col className="text-end">
                        {!summaryLoading && fieldData.budgetExceeded + fieldData.unallocated !== 0 && (
                            <Button size="sm" color="light" className="me-1 p-0 btn-text btn-border" onClick={quickFixExceeded}>
                                <IoHammerOutline size={20} style={{ color: '#111' }} />
                            </Button>
                        )}
                    </Col>
                </Row>
            </CardHeader>
            <CardHeader className="pt-1 pb-1 text-center">
                {summaryLoading ? <LoadSpinner /> : (
                    <>
                        <Row>
                            <Col className="text-muted cd-subheader">Budget</Col>
                            <Col className="text-muted cd-subheader">Unallocated</Col>
                            <Col className="text-muted cd-subheader">Exceeded</Col>
                        </Row>
                        <Row className="pt-2 pb-2">
                            <Col className="cd-value">{fieldData.budgetTotal?.toLocaleString('GB-en', currencyOptions)}</Col>
                            <Col className="cd-value">{fieldData.unallocated?.toLocaleString('GB-en', currencyOptions)}</Col>
                            <Col className="cd-value">{fieldData.budgetExceeded?.toLocaleString('GB-en', currencyOptions)}</Col>
                        </Row>
                    </>
                )}
            </CardHeader>
            <CardBody style={{ height: '1100px' }}>
                {budgetLoading ? <LoadSpinner /> : (
                    <div className="ag-theme-balham" style={{ height: '100%', width: '100%' }}>
                        <AgGridReact
                            rowData={budgetData}
                            columnDefs={columnDefs}
                            overlayNoRowsTemplate="No Records Found"
                            defaultColDef={defaultGridColumnDef}
                            pagination={true}
                            paginationPageSizeSelector={[10, 20, 50, 100]}
                            paginationPageSize={100}
                        />
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default DashboardBudgets;
